import { useState, useEffect, useCallback, useRef } from 'react';
import { Network, RefreshCw, AlertTriangle, TrendingUp, Zap } from 'lucide-react';
import { SeverityBadge } from './SEOOSShared';
import { Button } from '@/components/ui/button';

const API = '';

function ForceGraph({ data }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !data?.nodes?.length) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const W = canvas.width = canvas.parentElement.offsetWidth;
    const H = canvas.height = 500;

    const typeColors = { homepage: '#f59e0b', sport: '#22c55e', league: '#3b82f6', match: '#6366f1', bookmaker: '#ec4899', cup: '#a855f7', wc: '#ef4444', wc_sub: '#f97316', tool: '#06b6d4', cups: '#8b5cf6' };
    const typeRadius = { homepage: 18, sport: 14, league: 10, match: 5, bookmaker: 8, cup: 8, wc: 14, wc_sub: 8, tool: 8, cups: 10 };

    // Initialize positions
    const nodes = data.nodes.map((n, i) => ({
      ...n, x: W / 2 + (Math.random() - 0.5) * W * 0.8, y: H / 2 + (Math.random() - 0.5) * H * 0.8,
      vx: 0, vy: 0, r: typeRadius[n.type] || 6,
    }));
    // Homepage center
    const hp = nodes.find(n => n.type === 'homepage');
    if (hp) { hp.x = W / 2; hp.y = H / 2; }

    const nodeMap = {};
    nodes.forEach(n => nodeMap[n.id] = n);
    const edges = (data.edges || []).filter(e => nodeMap[e.source] && nodeMap[e.target]).slice(0, 300);

    let frame = 0;
    function simulate() {
      if (frame > 150) { draw(); return; }
      frame++;

      // Repulsion
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[j].x - nodes[i].x;
          const dy = nodes[j].y - nodes[i].y;
          const d = Math.max(Math.sqrt(dx * dx + dy * dy), 1);
          const f = 800 / (d * d);
          nodes[i].vx -= (dx / d) * f;
          nodes[i].vy -= (dy / d) * f;
          nodes[j].vx += (dx / d) * f;
          nodes[j].vy += (dy / d) * f;
        }
      }

      // Attraction along edges
      for (const e of edges) {
        const s = nodeMap[e.source], t = nodeMap[e.target];
        if (!s || !t) continue;
        const dx = t.x - s.x, dy = t.y - s.y;
        const d = Math.max(Math.sqrt(dx * dx + dy * dy), 1);
        const f = (d - 80) * 0.01;
        s.vx += (dx / d) * f;
        s.vy += (dy / d) * f;
        t.vx -= (dx / d) * f;
        t.vy -= (dy / d) * f;
      }

      // Center gravity
      for (const n of nodes) {
        n.vx += (W / 2 - n.x) * 0.001;
        n.vy += (H / 2 - n.y) * 0.001;
        n.vx *= 0.85; n.vy *= 0.85;
        n.x = Math.max(20, Math.min(W - 20, n.x + n.vx));
        n.y = Math.max(20, Math.min(H - 20, n.y + n.vy));
      }

      draw();
      requestAnimationFrame(simulate);
    }

    function draw() {
      ctx.fillStyle = '#0a0a1a';
      ctx.fillRect(0, 0, W, H);

      // Edges
      ctx.lineWidth = 0.5;
      for (const e of edges) {
        const s = nodeMap[e.source], t = nodeMap[e.target];
        if (!s || !t) continue;
        ctx.strokeStyle = 'rgba(147, 51, 234, 0.15)';
        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(t.x, t.y);
        ctx.stroke();
      }

      // Nodes
      for (const n of nodes) {
        const color = typeColors[n.type] || '#64748b';
        const glow = n.authority > 50;
        if (glow) {
          ctx.shadowColor = color;
          ctx.shadowBlur = 12;
        }
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;

        if (n.r >= 10) {
          ctx.fillStyle = '#fff';
          ctx.font = `${Math.max(8, n.r - 3)}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.fillText(n.label?.slice(0, 12), n.x, n.y + n.r + 12);
        }
      }

      // Legend
      ctx.font = '10px sans-serif';
      let lx = 10, ly = H - 15;
      for (const [type, color] of Object.entries(typeColors)) {
        ctx.fillStyle = color;
        ctx.fillRect(lx, ly, 8, 8);
        ctx.fillStyle = '#94a3b8';
        ctx.fillText(type, lx + 12, ly + 8);
        lx += ctx.measureText(type).width + 24;
      }
    }

    simulate();
  }, [data]);

  return <canvas ref={canvasRef} className="w-full rounded-xl border border-purple-500/20" style={{ height: 500 }} />;
}

export default function SEOOSLinkGraph() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  const token = localStorage.getItem('admin_token');
  const headers = { 'Authorization': `Bearer ${token}` };

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/seoos/link-graph`, { headers }).then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const rebuild = async () => {
    setBuilding(true);
    await fetch(`${API}/api/seoos/link-graph/rebuild`, { method: 'POST', headers });
    load();
    setBuilding(false);
  };

  if (loading || !data) return <div className="text-center py-16 text-slate-500">Yukleniyor...</div>;

  const { total_nodes, total_edges, orphan_count, clusters, strong_pages, weak_pages, boost_candidates, recommendations, graph_data, homepage_outlink_ratio } = data;

  return (
    <div className="space-y-6" data-testid="seoos-link-graph">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-black text-white" style={{ fontFamily: 'var(--font-heading)' }}>
          <Network className="w-5 h-5 inline mr-2 text-purple-400" />Ic Link Grafi & Otorite Akisi
        </h2>
        <Button onClick={rebuild} disabled={building} className="bg-purple-600 hover:bg-purple-500 text-white text-xs">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${building ? 'animate-spin' : ''}`} />
          {building ? 'Olusturuluyor...' : 'Grafi Yeniden Olustur'}
        </Button>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-slate-900/40 border border-purple-500/20 rounded-xl p-4 text-center">
          <p className="text-2xl font-black text-purple-400">{total_nodes}</p>
          <p className="text-[10px] text-slate-500 uppercase">Sayfa (Node)</p>
        </div>
        <div className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-4 text-center">
          <p className="text-2xl font-black text-blue-400">{total_edges}</p>
          <p className="text-[10px] text-slate-500 uppercase">Link (Edge)</p>
        </div>
        <div className="bg-slate-900/40 border border-red-500/20 rounded-xl p-4 text-center">
          <p className="text-2xl font-black text-red-400">{orphan_count}</p>
          <p className="text-[10px] text-slate-500 uppercase">Orphan Sayfa</p>
        </div>
        <div className="bg-slate-900/40 border border-amber-500/20 rounded-xl p-4 text-center">
          <p className="text-2xl font-black text-amber-400">%{homepage_outlink_ratio}</p>
          <p className="text-[10px] text-slate-500 uppercase">Ana Sayfa Dagitim</p>
        </div>
      </div>

      {/* Graph */}
      <ForceGraph data={graph_data} />

      {/* Clusters */}
      <div className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-5">
        <h3 className="text-sm font-bold text-white uppercase mb-4">Cluster Otorite Dagilimi</h3>
        <div className="grid grid-cols-5 gap-3">
          {Object.entries(clusters || {}).map(([type, info]) => (
            <div key={type} className="text-center p-3 rounded-lg bg-slate-800/30">
              <p className={`text-xl font-black ${info.avg_authority > 30 ? 'text-emerald-400' : info.avg_authority > 15 ? 'text-amber-400' : 'text-red-400'}`}>{info.avg_authority}</p>
              <p className="text-[10px] text-slate-500">{type} ({info.count})</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Strong Pages */}
        <div className="bg-slate-900/40 border border-emerald-500/20 rounded-xl p-5">
          <h3 className="text-sm font-bold text-emerald-400 uppercase mb-3"><TrendingUp className="w-4 h-4 inline mr-1" />En Guclu 10 Sayfa</h3>
          {strong_pages?.slice(0, 10).map((p, i) => (
            <div key={i} className="flex items-center gap-2 py-1.5 border-b border-slate-800/20">
              <span className="text-xs font-black text-emerald-400 w-8 text-right">{p.authority}</span>
              <span className="text-[10px] text-slate-500 w-16">{p.type}</span>
              <span className="text-xs text-slate-300 flex-1 truncate">{p.label}</span>
            </div>
          ))}
        </div>

        {/* Boost Candidates */}
        <div className="bg-slate-900/40 border border-amber-500/20 rounded-xl p-5">
          <h3 className="text-sm font-bold text-amber-400 uppercase mb-3"><Zap className="w-4 h-4 inline mr-1" />Boost Verilecek Sayfalar</h3>
          {boost_candidates?.slice(0, 10).map((p, i) => (
            <div key={i} className="flex items-center gap-2 py-1.5 border-b border-slate-800/20">
              <span className="text-xs font-black text-red-400 w-8 text-right">{p.authority}</span>
              <span className="text-[10px] text-slate-500 w-16">{p.type}</span>
              <span className="text-xs text-slate-300 flex-1 truncate">{p.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* AI Recommendations */}
      <div className="space-y-3">
        <h3 className="text-sm font-bold text-white uppercase">YZ Link Onerileri</h3>
        {recommendations?.map((rec, i) => (
          <div key={i} className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-bold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">{rec.priority}</span>
              <span className="text-sm font-bold text-white">{rec.title}</span>
            </div>
            <p className="text-xs text-slate-400 mb-2">{rec.detail}</p>
            <p className="text-[10px] text-purple-400">Cozum: {rec.fix}</p>
            <p className="text-[10px] text-emerald-400 mt-1">Etki: {rec.impact}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
